import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Portal,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdClose, MdSearch } from 'react-icons/md';
import { Cie10Entry, searchCie10 } from 'data/cie10';

interface Cie10ComboboxProps {
  value: string;
  onChange: (code: string) => void;
  onSelect?: (entry: Cie10Entry) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  isInvalid?: boolean;
}

export default function Cie10Combobox({
  value,
  onChange,
  onSelect,
  placeholder = 'Buscar por código o enfermedad…',
  size = 'sm',
  isInvalid,
}: Cie10ComboboxProps) {
  const [query, setQuery]       = useState(value);
  const [results, setResults]   = useState<Cie10Entry[]>([]);
  const [open, setOpen]         = useState(false);
  const [rect, setRect]         = useState<DOMRect | null>(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);
  const justPicked = useRef(false);

  const bg        = useColorModeValue('white', 'navy.800');
  const border    = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBg   = useColorModeValue('brand.50', 'navy.700');
  const activeBg  = useColorModeValue('brand.100', 'navy.600');
  const codeColor = useColorModeValue('brand.600', 'brand.300');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  // Sync external value → internal query
  useEffect(() => { setQuery(value); }, [value]);

  const openDropdown = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
    setActiveIdx(-1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val.toUpperCase().trim());
    setResults(searchCie10(val, 12));
    openDropdown();
  };

  const pick = (entry: Cie10Entry) => {
    justPicked.current = true;
    setQuery(entry.code);
    onChange(entry.code);
    onSelect?.(entry);
    setOpen(false);
    setResults([]);
    setTimeout(() => { justPicked.current = false; }, 100);
  };

  const clear = () => {
    setQuery('');
    onChange('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!inputRef.current?.contains(t) && !listRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pick(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Box position="relative">
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none" h="full">
          <MdSearch style={{ color: '#A0AEC0', width: 16, height: 16 }} />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (!justPicked.current && query) {
              setResults(searchCie10(query, 12));
              openDropdown();
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          borderRadius="7px"
          isInvalid={isInvalid}
          autoComplete="off"
          style={{ textTransform: 'uppercase' }}
        />
        {query && (
          <InputRightElement h="full" cursor="pointer" onClick={clear}>
            <MdClose style={{ color: '#A0AEC0', width: 14, height: 14 }} />
          </InputRightElement>
        )}
      </InputGroup>

      {open && results.length > 0 && rect && (
        <Portal>
          <Box
            ref={listRef}
            position="fixed"
            top={`${rect.bottom + 4}px`}
            left={`${rect.left}px`}
            width={`${Math.max(rect.width, 360)}px`}
            zIndex={9999}
            bg={bg}
            border="1px solid"
            borderColor={border}
            borderRadius="10px"
            boxShadow="0 8px 24px rgba(0,0,0,0.12)"
            maxH="240px"
            overflowY="auto"
            py="4px"
          >
            {results.map((entry, idx) => (
              <Flex
                key={entry.code}
                px="12px"
                py="8px"
                gap="10px"
                align="center"
                cursor="pointer"
                bg={idx === activeIdx ? activeBg : 'transparent'}
                _hover={{ bg: hoverBg }}
                onMouseDown={() => pick(entry)}
              >
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  color={codeColor}
                  flexShrink={0}
                  minW="52px"
                  fontFamily="mono"
                >
                  {entry.code}
                </Text>
                <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                  {entry.desc}
                </Text>
              </Flex>
            ))}
          </Box>
        </Portal>
      )}
    </Box>
  );
}
